import { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Users, Building, Truck, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { EarningsAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';
import { useNavigate } from 'react-router-dom';

export default function PaymentsOverviewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly');
  const [entityFilter, setEntityFilter] = useState<'all' | 'supplier' | 'transporter'>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, entityFilter]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { period };
      if (entityFilter !== 'all') {
        params.entityType = entityFilter;
      }
      const response = await EarningsAPI.getOverview(params);
      if (response.success) {
        setOverview(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching payments overview:', err);
      setError(err.message || 'Failed to load payments overview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading payments overview...</p>
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load overview'}</p>
          <Button onClick={fetchOverview}>Retry</Button>
        </div>
      </div>
    );
  }

  const platformEarnings = overview?.platformEarnings || {};
  const entityBreakdown = overview?.entityBreakdown || { supplier: {}, transporter: {} };
  const summary = overview?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Payments Overview</h1>
          <p className="text-muted-foreground">Comprehensive view of all platform payments</p>
        </div>
        <div className="flex gap-2">
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value as any)}
            className="px-4 py-2 border rounded-md"
          >
            <option value="all">All Entities</option>
            <option value="supplier">Suppliers Only</option>
            <option value="transporter">Transporters Only</option>
          </select>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="px-4 py-2 border rounded-md"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      {/* Platform Earnings Summary */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Platform Earnings</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(platformEarnings?.totalEarnings || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {platformEarnings?.totalTransactions || 0} transactions
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Paid Out</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(platformEarnings?.paidEarnings || 0)}</p>
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  {platformEarnings?.totalEarnings ? ((platformEarnings.paidEarnings / platformEarnings.totalEarnings) * 100).toFixed(1) : '0'}% completed
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(platformEarnings?.pendingEarnings || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Awaiting processing
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg per Transaction</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(platformEarnings?.totalEarnings && platformEarnings?.totalTransactions ? platformEarnings.totalEarnings / platformEarnings.totalTransactions : 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Commission rate
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Payouts Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Total Payouts Summary</CardTitle>
          <CardDescription>Aggregate view of all payments across entities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Total Payouts</p>
              <p className="text-3xl font-bold text-blue-600">{formatCurrency(summary?.totalPayouts || 0)}</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Paid</p>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(summary?.totalPaidPayouts || 0)}</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Pending</p>
              <p className="text-3xl font-bold text-orange-600">{formatCurrency(summary?.totalPendingPayouts || 0)}</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Failed</p>
              <p className="text-3xl font-bold text-red-600">{formatCurrency(summary?.totalFailedPayouts || 0)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entity Breakdown */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Supplier Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-blue-600" />
              Supplier Payments
            </CardTitle>
            <CardDescription>Payment breakdown for suppliers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium">Total Earnings</span>
                <span className="text-lg font-bold text-blue-600">
                  {formatCurrency(entityBreakdown?.supplier?.totalEarnings || 0)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-muted-foreground">Paid</span>
                  </div>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(entityBreakdown?.supplier?.paidEarnings || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entityBreakdown?.supplier?.paidTransactions || 0} txns
                  </p>
                </div>

                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-orange-600" />
                    <span className="text-xs text-muted-foreground">Pending</span>
                  </div>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatCurrency(entityBreakdown?.supplier?.pendingEarnings || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entityBreakdown?.supplier?.pendingTransactions || 0} txns
                  </p>
                </div>

                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-xs text-muted-foreground">Failed</span>
                  </div>
                  <p className="text-lg font-semibold text-red-600">
                    {formatCurrency(entityBreakdown?.supplier?.failedEarnings || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entityBreakdown?.supplier?.failedTransactions || 0} txns
                  </p>
                </div>

                <div className="p-3 border rounded-lg bg-blue-50">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-xs text-muted-foreground">Total</span>
                  </div>
                  <p className="text-lg font-semibold text-blue-600">
                    {entityBreakdown?.supplier?.totalTransactions || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    transactions
                  </p>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/earnings/pending-payouts?type=supplier')}
              >
                Manage Supplier Payouts
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transporter Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-green-600" />
              Transporter Payments
            </CardTitle>
            <CardDescription>Payment breakdown for transporters</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-sm font-medium">Total Earnings</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(entityBreakdown?.transporter?.totalEarnings || 0)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-muted-foreground">Paid</span>
                  </div>
                  <p className="text-lg font-semibold text-green-600">
                    {formatCurrency(entityBreakdown?.transporter?.paidEarnings || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entityBreakdown?.transporter?.paidTransactions || 0} txns
                  </p>
                </div>

                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-orange-600" />
                    <span className="text-xs text-muted-foreground">Pending</span>
                  </div>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatCurrency(entityBreakdown?.transporter?.pendingEarnings || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entityBreakdown?.transporter?.pendingTransactions || 0} txns
                  </p>
                </div>

                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-xs text-muted-foreground">Failed</span>
                  </div>
                  <p className="text-lg font-semibold text-red-600">
                    {formatCurrency(entityBreakdown?.transporter?.failedEarnings || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entityBreakdown?.transporter?.failedTransactions || 0} txns
                  </p>
                </div>

                <div className="p-3 border rounded-lg bg-green-50">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-muted-foreground">Total</span>
                  </div>
                  <p className="text-lg font-semibold text-green-600">
                    {entityBreakdown?.transporter?.totalTransactions || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    transactions
                  </p>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/earnings/pending-payouts?type=transporter')}
              >
                Manage Transporter Payouts
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common payment management tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/earnings/pending-payouts')}
            >
              <Clock className="h-6 w-6 text-orange-600" />
              <span className="font-semibold">Process Pending</span>
              <span className="text-xs text-muted-foreground">
                {formatCurrency(summary?.totalPendingPayouts || 0)}
              </span>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/earnings/history')}
            >
              <CheckCircle className="h-6 w-6 text-green-600" />
              <span className="font-semibold">View History</span>
              <span className="text-xs text-muted-foreground">
                All transactions
              </span>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/earnings/statistics')}
            >
              <TrendingUp className="h-6 w-6 text-blue-600" />
              <span className="font-semibold">Analytics</span>
              <span className="text-xs text-muted-foreground">
                Detailed stats
              </span>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/earnings/batch-status')}
            >
              <AlertCircle className="h-6 w-6 text-purple-600" />
              <span className="font-semibold">Batch Status</span>
              <span className="text-xs text-muted-foreground">
                Track batches
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
